import { api } from './api';

// Base URL for PDF endpoints
const PDF_BASE_URL = '/api/pdfs';
const CATEGORY_BASE_URL = '/api/pdf-categories';

// PDF CRUD operations
export const getPDFs = async (params = {}) => {
  return api.get(PDF_BASE_URL, { params });
};

export const getPDF = async (id) => {
  return api.get(`${PDF_BASE_URL}/${id}`);
};

export const createPDF = async (data) => {
  return api.post(PDF_BASE_URL, data);
};

export const updatePDF = async (id, data) => {
  return api.put(`${PDF_BASE_URL}/${id}`, data);
};

export const deletePDF = async (id) => {
  return api.delete(`${PDF_BASE_URL}/${id}`);
};

// PDF generation
export const generatePDF = async (id) => {
  return api.post(`${PDF_BASE_URL}/${id}/generate`);
};

// PDF duplication
export const duplicatePDF = async (id) => {
  return api.post(`${PDF_BASE_URL}/${id}/duplicate`);
};

// Sharing
export const sharePDF = async (id, users) => {
  return api.post(`${PDF_BASE_URL}/${id}/share`, { users });
};

export const unsharePDF = async (pdfId, userId) => {
  return api.delete(`${PDF_BASE_URL}/${pdfId}/share/${userId}`);
};

// Get shared PDFs
export const getSharedPDFs = async () => {
  return api.get(`${PDF_BASE_URL}/shared-with-me`);
};

// Analytics
export const getPDFAnalytics = async (id, dateRange, granularity = 'day') => {
  const params = { granularity };
  if (dateRange) {
    params.dateRange = `${dateRange.start},${dateRange.end}`;
  }
  return api.get(`${PDF_BASE_URL}/${id}/analytics`, { params });
};

// Track event
export const trackPDFEvent = async (pdfId, event, eventData) => {
  // Generate session ID if not exists
  let sessionId = localStorage.getItem('pdfSessionId');
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem('pdfSessionId', sessionId);
  }
  
  return api.post(`${PDF_BASE_URL}/${pdfId}/track`, {
    event,
    eventData,
    sessionId
  });
};

// Categories
export const getCategories = async () => {
  return api.get(CATEGORY_BASE_URL);
};

export const createCategory = async (data) => {
  return api.post(CATEGORY_BASE_URL, data);
};

export const updateCategory = async (id, data) => {
  return api.put(`${CATEGORY_BASE_URL}/${id}`, data);
};

export const deleteCategory = async (id) => {
  return api.delete(`${CATEGORY_BASE_URL}/${id}`);
};

// Public PDF URLs
export const getPDFUrls = (pdf) => {
  const baseUrl = window.location.origin;
  return {
    long: `${baseUrl}/pdf/view/${pdf._id}`,
    short: `${baseUrl}/p/${pdf.shortCode}`,
    slug: `${baseUrl}/docs/${pdf.slug}`,
    embed: `<iframe src="${baseUrl}/pdf/preview/${pdf._id}" width="100%" height="600" frameborder="0"></iframe>`
  };
};

// Generate download URL
export const getDownloadUrl = (pdfId) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/pdf/download/${pdfId}`;
};

// Check password
export const checkPDFPassword = async (id, password) => {
  try {
    const response = await api.post(`/pdf/check-password/${id}`, { password });
    return response.data.valid;
  } catch (error) {
    return false;
  }
};

// Helper function to generate session ID
const generateSessionId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Export formats
export const exportPDF = async (id, format) => {
  const response = await api.get(`${PDF_BASE_URL}/${id}/export`, {
    params: { format },
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `document.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return response;
};

// Revoke public access
export const revokePublicAccess = async (pdfId) => {
  const response = await api.post(`${PDF_BASE_URL}/${pdfId}/revoke-public`);
  return response.data;
};

// Emoji management
export const getRecentEmojis = async () => {
  return api.get(`${PDF_BASE_URL}/emojis/recent`);
};

export const addRecentEmoji = async (emoji) => {
  return api.post(`${PDF_BASE_URL}/emojis/recent`, { emoji });
};